import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import {
	LaboratoryRequisitionTemplate,
	LaboratoryRequisitionTemplatesWhereInput,
	PaginationOrderHow,
	useLaboratoryRequisitionTemplatesQuery,
} from '../../../../../graphql/graphql';
import { LaboratoryTemplateType } from '../../../Enums/LaboratoryTemplateType.enum';
import { useFlatDataList } from '../../../../Base/Hooks/useFlatDataList';
import styles from './LaboratoryRequestTemplateList.module.scss';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import FlatList from '../../../../Base/Components/FlatList';
import LaboratoryRequestTemplateListItem from '../LaboratoryRequestTemplateListItem/LaboratoryRequestTemplateListItem';

export type LaboratoryRequestTemplateListHandle = {
	refresh: () => void;
};

interface Props {
	type: LaboratoryTemplateType;
	doctorId: string;
	workplaceId: string;
}

const wrapperId = 'laboratoryRequestTemplateListWrapper';

const LaboratoryRequestTemplateList = forwardRef<
	LaboratoryRequestTemplateListHandle,
	Props
>(({ workplaceId, doctorId, type }, ref) => {
	const { flatData, pagination, getNextPage, appendData, updateItem, reset } =
		useFlatDataList<LaboratoryRequisitionTemplate>();

	const where: LaboratoryRequisitionTemplatesWhereInput = {};

	if (type === LaboratoryTemplateType.WORKPLACE) {
		where.workplaceId = workplaceId;
	} else if (type === LaboratoryTemplateType.DOCTOR) {
		where.doctorId = doctorId;
	}

	const { data, isLoading, refetch, isFetching } =
		useLaboratoryRequisitionTemplatesQuery({
			where,
			pagination: {
				...pagination,
				order: { key: 'created_at', how: PaginationOrderHow.Desc },
			},
		});

	useEffect(() => {
		appendData(
			(data?.laboratoryRequisitionTemplates
				?.data as LaboratoryRequisitionTemplate[]) ?? [],
		);
	}, [data]);

	const refresh = () => {
		reset();
		refetch();
	};

	useImperativeHandle(ref, () => ({ refresh }), [refresh]);

	return (
		<div className={styles.wrapper}>
			<ScrollContainer wrapperId={wrapperId} className={styles.list}>
				<FlatList
					onRefresh={refetch}
					onLoadMore={getNextPage}
					data={flatData}
					isLoading={isLoading || isFetching}
					hasMoreData={
						data?.laboratoryRequisitionTemplates?.meta?.hasNextPage || false
					}
					scrollableTargetId={wrapperId}
					renderItem={item => (
						<LaboratoryRequestTemplateListItem
							item={item}
							onUpdate={(item: LaboratoryRequisitionTemplate) =>
								updateItem(item)
							}
							onRemove={refresh}
							workplaceId={workplaceId}
						/>
					)}
				/>
			</ScrollContainer>
		</div>
	);
});

export default LaboratoryRequestTemplateList;
